import React from 'react';
import { DashboardAudience } from './types';

// =====================================================================
// AudienceBadge — who can see this dashboard.
// ---------------------------------------------------------------------
// The `audience` field is the only thing separating an internal dashboard
// from one a customer can open, and until now it was invisible outside the
// descriptor source. Surfacing it here means:
//
//   - staff can answer "what can customers see?" on the page they already
//     use, instead of visiting the provider mount to find out
//   - the director sees it while authoring, in the preview harness
//
// Inline styles on purpose: this renders both inside the tile design system
// (the preview) and on a plain Bootstrap admin page, and the portal's
// Bootstrap major version isn't something to depend on for badge classes.
// =====================================================================

const LABELS: Record<DashboardAudience, { text: string; bg: string; fg: string }> = {
    // Muted — the default, and the unremarkable case.
    internal: { text: 'Internal only', bg: '#eceae4', fg: '#52514e' },
    // Customer-visible cases stand out, because that is the fact worth noticing.
    external: { text: 'Providers only', bg: '#fde9c8', fg: '#7a4b00' },
    both: { text: 'Internal + Providers', bg: '#fde9c8', fg: '#7a4b00' },
};

export const audienceLabel = (audience: DashboardAudience): string =>
    LABELS[audience]?.text ?? String(audience);

export const AudienceBadge: React.FC<{
    audience: DashboardAudience;
    style?: React.CSSProperties;
}> = ({ audience, style }) => {
    const spec = LABELS[audience];
    if (!spec) return null;
    return (
        <span
            title={
                audience === 'internal'
                    ? 'Internal staff only. Not served to providers.'
                    : 'Reachable by providers — every query is scoped to their own facilities.'
            }
            style={{
                display: 'inline-block',
                padding: '0.1rem 0.45rem',
                borderRadius: 3,
                fontSize: '0.72rem',
                fontWeight: 600,
                lineHeight: 1.5,
                whiteSpace: 'nowrap',
                background: spec.bg,
                color: spec.fg,
                ...style,
            }}
        >
            {spec.text}
        </span>
    );
};
